'use client';
import React, { useRef } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'motion/react';
import { cn } from '@/lib/utils';

type ButtonProps<T extends React.ElementType> = {
  as?: T;
  borderRadius?: string;
  children: React.ReactNode;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
} & React.ComponentPropsWithRef<T>;

export function Button<T extends React.ElementType = 'button'>({
  borderRadius = '1.75rem',
  children,
  as: Component = 'button',
  containerClassName,
  borderClassName,
  duration,
  className,
  ...otherProps
}: ButtonProps<T>) {
  const COLORS = [
    'rgb(252, 210, 195)', // soft peach
    'rgb(187, 216, 230)', // light blue
    'rgb(174, 216, 199)', // mint green
    'rgb(242, 213, 163)', // warm beige
    'rgb(216, 209, 194)', // stone grey
  ];

  return (
    <Component
      className={cn(
        'relative h-14 w-45 overflow-hidden bg-white/10 dark:bg-black/10 p-[1px] text-xl m-5',
        containerClassName
      )}
      style={{ borderRadius, cursor: 'pointer' }}
      {...otherProps}
    >
      <div className="absolute inset-0" style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}>
        <MovingBorder duration={duration} rx="30%" ry="30%" colors={COLORS}>
          {/* The dot; color comes from MovingBorder via style.background */}
          <div className={cn('h-20 w-20 opacity-[0.9] blur-[1px]', borderClassName)} />
        </MovingBorder>
      </div>

      <div
        className={cn(
          'relative flex h-full w-full items-center justify-center bg-dark-900/[0.8] text-[18px] text-white antialiased backdrop-blur-xl',
          className
        )}
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        {children}
      </div>
    </Component>
  );
}

type MovingBorderProps<T extends React.ElementType = 'div'> = {
  children: React.ReactNode;
  duration?: number; // ms per loop
  rx?: string;
  ry?: string;
  colors?: string[]; // palette to cycle smoothly
} & React.ComponentPropsWithRef<T>;

export const MovingBorder = <T extends React.ElementType = 'div'>({
  children,
  duration = 3000,
  rx,
  ry,
  colors = [
    'rgb(252, 210, 195)',
    'rgb(187, 216, 230)',
    'rgb(174, 216, 199)',
    'rgb(242, 213, 163)',
    'rgb(216, 209, 194)',
  ],
  ...otherProps
}: MovingBorderProps<T>) => {
  const pathRef = useRef<SVGRectElement | null>(null);
  const progress = useMotionValue<number>(0);

  // Motion value for the radial gradient background
  const bg = useMotionValue<string>(`radial-gradient(${colors[0]} 40%, transparent 60%)`);

  // Helpers
  const toRgb = (c: string): [number, number, number] => {
    // supports #rrggbb, rgb(r,g,b), and CSS named via computed parsing
    if (c.startsWith('#')) {
      const hex = c.replace('#', '');
      const n = hex.length === 3
        ? hex.split('').map((h) => h + h).join('')
        : hex;
      const r = parseInt(n.slice(0, 2), 16);
      const g = parseInt(n.slice(2, 4), 16);
      const b = parseInt(n.slice(4, 6), 16);
      return [r, g, b];
    }
    if (c.startsWith('rgb')) {
      const nums = c.match(/\d+(\.\d+)?/g) || ['0', '0', '0'];
      return [Number(nums[0]), Number(nums[1]), Number(nums[2])];
    }
    // Fallback via canvas (rarely needed)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = c;
    const computed = ctx.fillStyle as string; // returns rgb(...)
    const nums = computed.match(/\d+(\.\d+)?/g) || ['0', '0', '0'];
    return [Number(nums[0]), Number(nums[1]), Number(nums[2])];
  };

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength();
    if (!length) return;

    const pxPerMs = length / duration;
    const p = (time * pxPerMs) % length; // 0..length
    progress.set(p);

    // Normalize to [0..1], then map across segments
    const t = p / length; // 0..1
    const segFloat = t * colors.length;
    const i = Math.floor(segFloat) % colors.length;
    const j = (i + 1) % colors.length;
    const f = segFloat - Math.floor(segFloat); // fractional part 0..1

    const [r1, g1, b1] = toRgb(colors[i]);
    const [r2, g2, b2] = toRgb(colors[j]);

    const r = Math.round(lerp(r1, r2, f));
    const g = Math.round(lerp(g1, g2, f));
    const b = Math.round(lerp(b1, b2, f));

    // Smoothly blended color along the path
    bg.set(`radial-gradient(rgb(${r}, ${g}, ${b}) 40%, transparent 60%)`);
  });

  const x = useTransform(progress, (val) => pathRef.current?.getPointAtLength(val).x);
  const y = useTransform(progress, (val) => pathRef.current?.getPointAtLength(val).y);
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        {...otherProps}
      >
        <rect fill="none" width="100%" height="100%" rx={rx} ry={ry} ref={pathRef} />
      </svg>

      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'inline-block',
          transform,
          background: bg,              // <- blended gradient background
          borderRadius: '9999px',
        }}
      >
        {children}
      </motion.div>
    </>
  );
};
